import { useEffect, useRef } from "react";

function rr(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  w: number,
  h: number,
  r: number
) {
  r = Math.min(r, w / 2, h / 2);
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.arcTo(x + w, y, x + w, y + h, r);
  ctx.arcTo(x + w, y + h, x, y + h, r);
  ctx.arcTo(x, y + h, x, y, r);
  ctx.arcTo(x, y, x + w, y, r);
  ctx.closePath();
}

function readingScrim(
  ctx: CanvasRenderingContext2D,
  cw: number,
  ch: number,
  dark: boolean
) {
  const [br, bg, bb] = dark ? [5, 5, 5] : [255, 255, 255];
  const cx = cw / 2,
    cy = ch / 2;
  const rad = Math.min(cw, ch) * 0.62;
  const g = ctx.createRadialGradient(cx, cy, 0, cx, cy, rad);
  g.addColorStop(0, `rgba(${br},${bg},${bb},1)`);
  g.addColorStop(0.42, `rgba(${br},${bg},${bb},0.92)`);
  g.addColorStop(0.7, `rgba(${br},${bg},${bb},0.45)`);
  g.addColorStop(1, `rgba(${br},${bg},${bb},0)`);
  ctx.fillStyle = g;
  ctx.fillRect(0, 0, cw, ch);
}

export function NotFoundCanvas() {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d")!;
    let raf: number;
    const dpr = window.devicePixelRatio || 1;

    function resize() {
      const w = canvas!.offsetWidth,
        h = canvas!.offsetHeight;
      canvas!.width = w * dpr;
      canvas!.height = h * dpr;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    }
    const ro = new ResizeObserver(resize);
    ro.observe(canvas);
    resize();

    const COLS = 7,
      ROWS = 5;
    let frame = 0,
      lostHome = 8,
      drift = 0;

    function draw() {
      frame++;
      const t = frame * 0.01;
      const dark = document.documentElement.classList.contains("dark");
      const cw = canvas!.offsetWidth,
        ch = canvas!.offsetHeight;
      const [br, bg, bb] = dark ? [5, 5, 5] : [255, 255, 255];
      const [fr, fg, fb] = dark ? [245, 245, 245] : [10, 10, 10];
      ctx.fillStyle = `rgb(${br},${bg},${bb})`;
      ctx.fillRect(0, 0, cw, ch);
      const cell = Math.min(cw / (COLS + 3), ch / (ROWS + 3));
      const gap = cell * 0.22,
        sz = cell - gap;
      const gw = COLS * cell,
        gh = ROWS * cell;
      const ox = (cw - gw) / 2,
        oy = (ch - gh) / 2;
      drift += 0.0065;
      if (drift > 1.25) {
        drift = 0;
        lostHome = Math.floor(Math.random() * COLS * ROWS);
      }
      for (let r = 0; r < ROWS; r++) {
        for (let c = 0; c < COLS; c++) {
          const idx = r * COLS + c;
          const x = ox + c * cell + gap / 2,
            y = oy + r * cell + gap / 2;
          const breathe = Math.sin(t + idx * 0.4) * 0.04;
          if (idx === lostHome) {
            const e = drift,
              ease = e < 1 ? e * e * (3 - 2 * e) : 1;
            const lx =
              x + sz / 2 + Math.sin(idx) * 0.5 * cell * ease * 2.2;
            const ly = y + sz / 2 - cell * ease * 3.4;
            const a = Math.max(0, 1 - e * 0.85);
            ctx.save();
            ctx.translate(lx, ly);
            ctx.rotate(ease * 0.5 * (idx % 2 ? 1 : -1));
            rr(ctx, -sz / 2, -sz / 2, sz, sz, sz * 0.22);
            ctx.fillStyle = `rgba(${fr},${fg},${fb},${a})`;
            ctx.fill();
            ctx.restore();
          } else {
            rr(ctx, x, y, sz, sz, sz * 0.22);
            ctx.strokeStyle = `rgba(${fr},${fg},${fb},${(dark ? 0.18 : 0.14) + breathe})`;
            ctx.lineWidth = 1;
            ctx.stroke();
          }
        }
      }
      readingScrim(ctx, cw, ch, dark);
      raf = requestAnimationFrame(draw);
    }

    draw();
    return () => {
      cancelAnimationFrame(raf);
      ro.disconnect();
    };
  }, []);

  return <canvas ref={canvasRef} className="absolute inset-0 h-full w-full" />;
}
